<script setup lang="ts">
const logo = useTemplateRef<HTMLDivElement>('logo')
const tagline = useTemplateRef<HTMLParagraphElement>('tagline')
const image = useTemplateRef<HTMLDivElement>('image')
const { y: windowScrollY } = useWindowScroll()
const logoIsVisible = useElementVisibility(logo)
const isReadyToShowNavbar = ref(false)
const isDark = useDark()
// Temporarily hardcoded to summer
const season = computed(() => {
  // const month = new Date().getMonth()

  // if (month <= 3 || month >= 9) {
  //   return 'winter'
  // }

  return 'summer'
})
const products = ref([
  {
    title: 'The Show Portal',
    description:
      'Create and manage your shows, collect exhibitor information, fees, and sell merch.',
    icon: 'lucide:ticket',
    to: 'https://theshowportal.com',
    class: 'lg:col-span-2',
    image: {
      path: 'https://theshowportal.com/logo.png',
      height: 140,
    },
    orientation: 'horizontal',
  },
  {
    title: 'The Sort',
    description:
      'Track judging scores and provide a competitive edge for college recruitment.',
    icon: 'lucide:arrow-down-up',
    to: 'https://thesortlsj.com',
    variant: 'soft',
  },
  {
    title: 'Horse Hotel',
    description:
      'List your location and allow travellers and locals to book your space.',
    icon: 'lucide:hotel',
    to: 'https://horsehotel.net',
    variant: 'soft',
  },
  {
    title: 'Video Sale Book',
    description:
      'Turn your sale book into a mobile friendly, clickable catalog - tap a lot, watch the video, track engagement.',
    icon: 'lucide:video',
    to: 'https://videosalebook.com',
    image: {
      path: 'suite/video-sale-book',
      height: 140,
    },
    class: 'lg:col-span-2',
    orientation: 'horizontal',
    reverse: true,
  },
  {
    title: 'Advertising',
    description: 'Personalized advertising for your business.',
    icon: 'lucide:dollar-sign',
    to: 'mailto:contact@tarc.ltd',
    class: 'lg:col-span-3',
    target: '_blank',
    variant: 'soft',
  },
])

function updateLogo(scrollY: number) {
  if (!logo.value) {
    return
  }

  const progress = Math.min(scrollY / 300, 1)
  const translateY = -284 * progress
  const scale = 1 - 0.67 * progress

  logo.value.style.setProperty(
    'transform',
    `translateY(${translateY}px) scale(${scale})`,
  )
}

function updateTagline(scrollY: number) {
  if (!tagline.value) {
    return
  }

  const progress = Math.min(scrollY / 400, 1)
  const opacity = progress

  tagline.value.style.setProperty('opacity', `${opacity}`)
}

function updateImage(scrollY: number) {
  if (!image.value) {
    return
  }

  const progress = Math.min(scrollY / 400, 1)
  const opacity = 1 - progress
  const blur = progress * 25

  image.value.style.setProperty('opacity', `${opacity}`)
  image.value.style.setProperty('filter', `blur(${blur}px)`)
}

whenever(image, () => {
  updateImage(windowScrollY.value)
})

whenever(logo, () => {
  updateLogo(windowScrollY.value)
})

whenever(tagline, () => {
  updateTagline(windowScrollY.value)
})

useEventListener('scroll', () => {
  const scrollY = windowScrollY.value
  updateLogo(scrollY)
  updateTagline(scrollY)
  updateImage(scrollY)
})

onMounted(() => {
  setTimeout(() => {
    isReadyToShowNavbar.value = true
  }, 750)
})

useHead({
  titleTemplate: '%s',
})
</script>

<template>
  <Transition
    name="fade"
    mode="out-in"
    appear
  >
    <div
      v-if="isReadyToShowNavbar && !logoIsVisible"
      class="fixed top-0 left-0 right-0 bg-(--ui-bg) py-4 z-50"
    >
      <UContainer class="px-8">
        <NuxtLink
          class="flex w-fit"
          to="/"
          alt="TARC"
          aria-label="TARC"
        >
          <img
            class="h-6"
            src="/logo-text-gold.svg"
            alt="TARC Logo"
            loading="preload"
            draggable="false"
          />
        </NuxtLink>
      </UContainer>
    </div>
  </Transition>
  <div class="w-full">
    <div class="h-[900px]">
      <div
        ref="image"
        class="fixed inset-0 z-0"
      >
        <picture loading="eager">
          <source
            :srcset="`/cow-${season}.avif`"
            type="image/avif"
          />
          <source
            :srcset="`/cow-${season}.webp`"
            type="image/webp"
          />
          <img
            :src="`/cow-${season}.jpg`"
            type="image/jpeg"
            class="size-full object-cover select-none pointer-events-none"
            alt="Cow photo"
            draggable="false"
          />
        </picture>
      </div>
      <div
        ref="logo"
        class="sticky w-[80%] lg:w-1/2 top-[300px] mx-auto z-10"
      >
        <img
          src="/logo-full-gold.svg"
          class="w-full select-none pointer-events-none"
          alt="TARC Logo"
          draggable="false"
          loading="preload"
        />
      </div>
      <UContainer class="sticky top-[800px] px-8 pb-[200px] z-10">
        <p
          ref="tagline"
          class="w-fit mx-auto text-2xl opacity-0 lg:text-4xl font-splash"
        >
          Preserving what matters,<br />
          and elevating what's possible.
        </p>
      </UContainer>
    </div>
    <div class="z-10">
      <UContainer class="grid gap-6 px-8 pb-16">
        <h2 class="text-lg font-bold">
          Products
        </h2>
        <UPageGrid>
          <UPageCard
            v-for="(card, index) in products"
            :key="index"
            v-bind="card"
            :spotlight="Boolean(card.image)"
          >
            <template v-if="card.title === 'Video Sale Book'">
              <ClientOnly>
                <picture
                  loading="lazy"
                >
                  <source
                    :srcset="`${card.image.path}-${isDark ? 'dark' : 'light'}.avif`"
                    type="image/avif"
                  />
                  <source
                    :srcset="`${card.image.path}-${isDark ? 'dark' : 'light'}.webp`"
                    type="image/webp"
                  />
                  <img
                    :src="`${card.image.path}-${isDark ? 'dark' : 'light'}.jpg`"
                    type="image/jpeg"
                    :height="card.image.height"
                    :width="card.image.width"
                    class="w-full select-none pointer-events-none"
                    :alt="`${card.title} showcase image`"
                    draggable="false"
                  />
                </picture>
              </ClientOnly>
            </template>
            <img
              v-else-if="card.image"
              loading="lazy"
              :src="card.image.path"
              :height="card.image.height"
              :width="card.image.width"
              class="w-full py-8 select-none pointer-events-none"
              :alt="`${card.title} showcase image`"
              draggable="false"
            />
          </UPageCard>
        </UPageGrid>
      </UContainer>
      <UContainer class="px-8 pb-16 grid gap-6">
        <h2 class="text-lg font-bold">
          Contact
        </h2>
        <p class="text-(--ui-text-muted)">
          We're always looking for new projects and collaborations.
        </p>
        <UPageCard
          class="text-center py-10"
          spotlight
        >
          <div class="flex flex-col gap-8 items-center">
            <p class="text font-medium">
              If you're interested in working with us, please reach out!
            </p>
            <UButton
              leading-icon="lucide:mail"
              variant="link"
              class="px-0 w-fit"
              to="mailto:contact@tarc.ltd"
              target="_blank"
            >
              contact@tarc.ltd
            </UButton>
          </div>
        </UPageCard>
      </UContainer>
    </div>
  </div>
</template>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}
</style>
